import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { useDragEngine } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import { LetterTile, resolveAsset, seededShuffle, useCompleteOnce } from "./engineUtils";
import { Counter, IconArrow, IconCheck, ROOT_STYLE, RoundButton, SparkleBurst, frost, useLetterAccent } from "./tapUtils";

function tokenize(text) {
  return String(text || "").match(/\S+/g) || [];
}

function coreWord(tok) {
  return String(tok).replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

/**
 * Page-turn story reader. Each page shows its art large with the text under
 * it; when a page has focus words (`bWords` / `focusWords`) they are lifted
 * out into word chips the child drags (or taps) back into the blanks.
 * Completes when the learner turns past the last page.
 */
export default function StoryPagesGame({ letter, config = {}, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const bp = useBreakpoint();
  const wide = bp === "md" || bp === "lg";

  const pages = useMemo(() => {
    const all = config.pages || [];
    const n = config.pagesPerRound || all.length || 1;
    return seededShuffle(all, 88).slice(0, Math.min(n, all.length));
  }, [config.pages, config.pagesPerRound]);

  const [pi, setPi] = useState(0);
  const [dir, setDir] = useState(1);
  const [finished, setFinished] = useState(false);
  const page = pages[pi];
  const blankWords = useMemo(() => page?.bWords || page?.focusWords || [], [page]);
  const [filled, setFilled] = useState([]);
  const [pool, setPool] = useState([]); // [{ id, word }]
  const [selected, setSelected] = useState(null); // token id (tap-to-place)
  const [wrongBlank, setWrongBlank] = useState({});
  const [placedTick, setPlacedTick] = useState({});
  const filledRef = useRef(filled);
  filledRef.current = filled;
  const poolRef = useRef(pool);
  poolRef.current = pool;

  // New page: reset blanks + chips, read the page aloud.
  useEffect(() => {
    if (!page) return;
    setFilled(Array(blankWords.length).fill(null));
    setPool(seededShuffle(blankWords.map((w, i) => ({ id: `${w}-${i}`, word: w })), 90 + pi));
    setSelected(null);
    speak?.(page.text);
  }, [pi, page, blankWords, speak]);

  const blanksReady = blankWords.length === 0 || (filled.length === blankWords.length && filled.every(Boolean));
  const allDone = blanksReady && blankWords.length > 0;

  useEffect(() => {
    if (allDone) sfx?.("correct");
  }, [allDone, sfx]);

  /** Try to put token `id` into blank `idx`. Returns true when it fits. */
  const tryPlace = useCallback(
    (id, idx) => {
      const token = poolRef.current.find((t) => t.id === id);
      if (!token || idx == null || idx < 0 || filledRef.current[idx]) return false;
      if (String(token.word).toLowerCase() !== String(blankWords[idx]).toLowerCase()) {
        onWrong?.();
        setWrongBlank((m) => ({ ...m, [idx]: (m[idx] || 0) + 1 }));
        return false;
      }
      sfx?.("snap");
      speak?.(token.word);
      setFilled((f) => { const n = [...f]; n[idx] = token.word; return n; });
      setPool((p) => p.filter((t) => t.id !== id));
      setPlacedTick((m) => ({ ...m, [idx]: (m[idx] || 0) + 1 }));
      setSelected(null);
      return true;
    },
    [blankWords, onWrong, sfx, speak],
  );

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => { setSelected(null); sfx?.("pickup", { volume: 0.5 }); },
    onDrop: ({ payload, targetId, targetData }) => {
      if (!targetId) return false;
      return tryPlace(payload, targetData?.idx);
    },
  });

  const turn = (delta) => {
    if (finished) return;
    if (delta > 0 && !blanksReady) return;
    if (delta > 0 && pi + 1 >= pages.length) {
      sfx?.("pageturn");
      setFinished(true);
      finish();
      return;
    }
    const next = pi + delta;
    if (next < 0 || next >= pages.length) return;
    sfx?.("pageturn");
    setDir(delta);
    setPi(next);
  };

  if (!page) {
    return <div style={ROOT_STYLE} />;
  }

  const tokens = tokenize(page.text);
  let blankIdx = 0;
  const art = resolveAsset(letter, page.imageAssetName);

  const renderChip = (token, ghost = false) => (
    <LetterTile text={token.word} wide scale={0.7} accent={accent} deep={deep} selected={selected === token.id && !ghost} />
  );

  return (
    <div style={ROOT_STYLE}>
      <ConfettiBurst active={finished} originY={40} count={36} reduceMotion={reduceMotion} />
      <Counter done={pi + 1} total={pages.length} accent={accent} label={`Page ${pi + 1} of ${pages.length}`} />
      <div style={{ flex: 1, minHeight: 0, position: "relative", perspective: 1400 }}>
        <AnimatePresence mode="wait" initial={false} custom={dir}>
          <motion.div
            key={pi}
            custom={dir}
            initial={reduceMotion ? false : { x: dir * 80, rotateY: dir * -18, opacity: 0 }}
            animate={{ x: 0, rotateY: 0, opacity: 1 }}
            exit={reduceMotion ? undefined : { x: dir * -80, rotateY: dir * 18, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            style={{
              ...frost({ background: "rgba(255,255,255,0.95)" }),
              position: "absolute", inset: 0, transformOrigin: dir > 0 ? "left center" : "right center",
              display: "flex", flexDirection: wide ? "row" : "column", gap: wide ? 16 : 10, padding: wide ? 16 : 12, boxSizing: "border-box", overflow: "hidden",
            }}
          >
            {/* Art */}
            <div style={{ flex: wide ? "0 0 46%" : "1 1 0", minHeight: wide ? 0 : "32%", minWidth: 0, borderRadius: LEARN.radius.md, overflow: "hidden", background: `linear-gradient(180deg, ${LEARN.color.sky}66, ${LEARN.color.blueSoft})`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
              {art && (
                <img src={art} alt={`Story page ${page.pageNumber ?? pi + 1}`} draggable={false} decoding="async" style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
              )}
            </div>

            {/* Text + chips + nav (the page counter floats top-right, so the text starts under it on the wide layout) */}
            <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 10, paddingTop: wide ? 26 : 0 }}>
              <p style={{ margin: 0, flex: "0 1 auto", minHeight: "2.6em", overflow: "auto", fontSize: "clamp(19px, calc(var(--tile, 72px) * 0.28), 25px)", fontWeight: 600, lineHeight: 1.7, color: LEARN.color.ink }}>
                {tokens.map((tok, i) => {
                  const core = coreWord(tok);
                  const isBlank = blankIdx < blankWords.length && core.toLowerCase() === String(blankWords[blankIdx]).toLowerCase();
                  if (!isBlank) return <span key={i}>{tok} </span>;
                  const bi = blankIdx;
                  blankIdx += 1;
                  const lead = tok.slice(0, tok.indexOf(core));
                  const trail = tok.slice(tok.indexOf(core) + core.length);
                  const word = filled[bi];
                  const targetId = `blank-${pi}-${bi}`;
                  const hovering = hoverId === targetId;
                  return (
                    <span key={`blank-${bi}`} style={{ whiteSpace: "nowrap" }}>
                      {lead}
                      <BlankSlot
                        targetRef={(el) => registerTarget(targetId, el, { idx: bi })}
                        word={word}
                        index={bi}
                        onTap={() => { if (!word && selected) tryPlace(selected, bi); }}
                        wrongTick={wrongBlank[bi] || 0}
                        placedTick={placedTick[bi] || 0}
                        hovering={hovering}
                        armed={!!selected && !word}
                        accent={accent}
                        deep={deep}
                        reduceMotion={reduceMotion}
                      />
                      {trail}{" "}
                    </span>
                  );
                })}
              </p>

              {blankWords.length > 0 && (
                <div role="group" aria-label="Word chips" style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", alignItems: "center", minHeight: 52, padding: 4, borderRadius: LEARN.radius.md, background: `${accent}0f` }}>
                  <AnimatePresence initial={false}>
                    {pool.map((token) => (
                      <motion.button
                        key={token.id}
                        type="button"
                        layout={!reduceMotion}
                        initial={reduceMotion ? false : { scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.4, opacity: 0, transition: { duration: 0.15 } }}
                        aria-label={`${token.word} chip${selected === token.id ? ", selected" : ""}`}
                        aria-pressed={selected === token.id}
                        {...dragProps(token.id, () => renderChip(token, true))}
                        className="learn-piece"
                        onClick={() => { sfx?.("pop", { volume: 0.5 }); speak?.(token.word, { interrupt: true }); setSelected((s) => (s === token.id ? null : token.id)); }}
                        style={{ padding: 0, border: "none", background: "none", cursor: "grab", touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent", borderRadius: "calc(var(--tile, 72px) * 0.7 * 0.22)" }}
                      >
                        {renderChip(token)}
                      </motion.button>
                    ))}
                  </AnimatePresence>
                  {pool.length === 0 && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: LEARN.color.mintDeep }}>
                      <span style={{ width: 26, height: 26, borderRadius: "50%", background: LEARN.color.mint, display: "grid", placeItems: "center" }}><IconCheck size={15} /></span>
                      All the {letter} words are in place
                    </span>
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: "auto" }}>
                <RoundButton ariaLabel="Previous page" onClick={() => turn(-1)} disabled={pi === 0 || finished} accent={accent} deep={deep} reduceMotion={reduceMotion} size={50}>
                  <IconArrow dir="left" />
                </RoundButton>
                <span style={{ fontSize: 16, fontWeight: 700, color: blanksReady ? deep : LEARN.color.muted, textAlign: "center", transition: "color 200ms ease" }}>
                  {finished ? "The end" : blanksReady ? (pi + 1 >= pages.length ? "Turn the page to finish" : "Turn the page") : `Fill the ${letter} words`}
                </span>
                <motion.div animate={blanksReady && !finished && !reduceMotion ? { scale: [1, 1.08, 1] } : { scale: 1 }} transition={{ duration: 1.4, repeat: blanksReady && !finished ? Infinity : 0, repeatDelay: 0.6 }}>
                  <RoundButton ariaLabel={pi + 1 >= pages.length ? "Finish story" : "Next page"} onClick={() => turn(1)} disabled={!blanksReady || finished} accent={accent} deep={deep} reduceMotion={reduceMotion} size={50}>
                    <IconArrow dir="right" />
                  </RoundButton>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <GhostLayer />
    </div>
  );
}

/** One gap in the sentence: a drop target that also accepts a tapped chip. */
function BlankSlot({ targetRef, word, index, onTap, wrongTick, placedTick, hovering, armed, accent, deep, reduceMotion }) {
  const controls = useAnimationControls();
  useEffect(() => {
    if (!wrongTick || reduceMotion) return;
    controls.start({ x: [0, -7, 7, -5, 5, 0], transition: { duration: 0.4 } });
  }, [wrongTick, reduceMotion, controls]);
  return (
    <motion.button
      type="button"
      ref={targetRef}
      animate={controls}
      aria-label={word || `Blank ${index + 1}`}
      onClick={onTap}
      className={armed && !word ? "learn-slot-armed" : undefined}
      style={{
        "--accent": accent, "--accent-soft": `${accent}22`, "--accent-ring": `${accent}33`,
        position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle",
        minWidth: "3.2em", minHeight: "1.7em", margin: "0 0.1em", padding: "0 0.5em", borderRadius: 12, cursor: word ? "default" : "pointer",
        fontFamily: LEARN.font, fontWeight: 700, fontSize: "inherit", lineHeight: 1.3,
        background: word ? accent : hovering || armed ? `${accent}22` : "rgba(11,26,51,0.05)",
        color: word ? "#fff" : LEARN.color.ink,
        border: word ? `2px solid ${accent}` : `2px dashed ${hovering || armed ? accent : `${accent}88`}`,
        boxShadow: word ? `0 3px 0 ${deep}` : hovering ? `0 0 0 3px ${accent}55` : "none",
        textShadow: word ? "0 1px 0 rgba(11,26,51,0.18)" : "none",
        transition: reduceMotion ? "none" : "background 160ms, border-color 160ms, box-shadow 160ms",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {word ? (
        <motion.span initial={reduceMotion ? false : { scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 420, damping: 22 }}>{word}</motion.span>
      ) : " "}
      <SparkleBurst trigger={placedTick} color={accent} reduceMotion={reduceMotion} distance={34} count={6} />
    </motion.button>
  );
}
